import {queryOptions} from '@tanstack/react-query'
import {notFound} from '@tanstack/react-router'
import imageUrlBuilder from '@sanity/image-url'
import {createServerFn} from '@tanstack/react-start'
import {z} from 'zod'
import fetchDesignCollectionDetails from '~/client/spreadshop/old/fetchDesignCollectionDetails'
import fetchCollection from '~/client/sanity/fetchCollection'
import {applyServerFnCache} from '~/client/fn/utils'
import {stringToNumber} from '~/utils/number'
import {shuffleBySeed} from '~/utils/random'
import client from '~/client/sanity/Client'
import {Locale} from '~/gql/graphql'

const IMAGE_BUILDER = imageUrlBuilder(client)
const CACHE_TIME_SEC = 60 * 60 * 3 // 3h

const collectionParams = z.object({
    collectionId: z.string(),
    locale: z.nativeEnum(Locale).optional(),
})

export const getCollectionDetails = createServerFn({
    method: 'GET',
})
    .inputValidator((data: unknown) => {
        const result = collectionParams.safeParse(data)
        if (result.error) {
            throw notFound()
        }

        return result.data
    })
    .handler(async ({data}) => {
        const {collectionId, locale} = data

        const [sprdCollectionDetails, sanityCollectionDetails] = await Promise.all([
            fetchDesignCollectionDetails(collectionId, locale),
            fetchCollection(collectionId, locale),
        ])

        if (!sprdCollectionDetails) {
            return undefined
        }

        applyServerFnCache(CACHE_TIME_SEC)

        return {
            ...sprdCollectionDetails,
            designs: shuffleBySeed(
                sprdCollectionDetails.designs,
                stringToNumber(sprdCollectionDetails.id)
            ),
            name: sanityCollectionDetails?.overwrites?.title ?? sprdCollectionDetails.name,
            description:
                sanityCollectionDetails?.overwrites?.description ??
                sprdCollectionDetails.description,
            seo: {
                ...sanityCollectionDetails?.seo,
                image: sanityCollectionDetails?.seo?.image
                    ? IMAGE_BUILDER.image(sanityCollectionDetails.seo.image)
                          .width(1200)
                          .height(630)
                          .url()
                    : undefined,
            },
        }
    })

export const collectionDetailsQueryOptions = (collectionId: string, locale: Locale = Locale.EnEu) =>
    queryOptions({
        queryKey: [locale, 'collections', collectionId],
        queryFn: () => getCollectionDetails({data: {collectionId, locale}}),
        gcTime: 1000 * CACHE_TIME_SEC, // 3h
        staleTime: 1000 * CACHE_TIME_SEC, // 3h
    })
